import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { queryKeys } from '@/queries/_shared/queryKeys';
import { feedEndpoints, socialClient, unwrapBody } from '@/queries/feed/client';

function normalizeRegions(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function mapWeather(regions) {
  const now = Math.floor(Date.now() / 1000);
  return regions
    .map((region) => {
      const list = Array.isArray(region?.weather?.list) ? [...region.weather.list] : [];
      if (!list.length) return null;

      list.sort((a, b) => Number(a?.dt || 0) - Number(b?.dt || 0));
      const withNow = [...list, { dt: now + 0.1 }].sort((a, b) => Number(a?.dt || 0) - Number(b?.dt || 0));
      const nowIndex = withNow.findIndex((item) => item.dt === now + 0.1);
      const start = Math.max(nowIndex - 1, 0);
      const fcasts = list.slice(start, start + 6);
      if (!fcasts.length) return null;

      const seasonIndex = Math.floor((((new Date().getMonth() + 1) % 12) / 3));
      const seasons = ['winter', 'spring', 'summer', 'autumn'];
      const casts = fcasts.map((cast) => ({
        ...cast,
        time: new Date(Number(cast.dt || 0) * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
        main: {
          ...(cast.main || {}),
          temp: Math.floor(Number(cast?.main?.temp || 0))
        },
        wind: {
          ...(cast.wind || {}),
          speed: Math.floor(Number(cast?.wind?.speed || 0))
        }
      }));

      return {
        caption: region.caption || '',
        first: casts[0],
        casts: casts.slice(1),
        season: seasons[seasonIndex] || 'winter'
      };
    })
    .filter(Boolean);
}

async function fetchRegionsWeather(regionIds) {
  if (!regionIds.length) return [];
  const response = await socialClient.get(feedEndpoints.regions, { params: { ids: regionIds } });
  const body = unwrapBody(response.data);
  return mapWeather(normalizeRegions(body));
}

export function useFeedWeatherQuery(regionIdsRef) {
  const queryRegionIds = computed(() => (Array.isArray(regionIdsRef?.value) ? regionIdsRef.value : []));

  return useQuery({
    queryKey: computed(() => queryKeys.feed.weather(queryRegionIds.value)),
    queryFn: () => fetchRegionsWeather(queryRegionIds.value),
    enabled: computed(() => queryRegionIds.value.length > 0)
  });
}
